import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { errorMessage } from '../errors';
import { Alert, Button, Card, PageHeader } from '../ui';
import { LogoutIcon, MailIcon } from '../ui/icons';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { initials } from '../shell/collections';
import { sectionCrumb } from './SectionLayout';

/**
 * /settings/account — who is logged in, the interface's language (kept on
 * the account, see docs/decisions.md, slice 8), a link to choose a new
 * password (the e-mail of "Forgot your password?", sent to oneself: there is
 * no password form), inviting someone, logging out.
 */
function AccountSettings({ user, onInvite, onLogout }) {
  const { t } = useTranslation();
  const isAdmin = user.role === 'admin';
  const [link, setLink] = useState(null);   // null | 'sending' | 'sent' | error text
  const sendLink = async () => {
    setLink('sending');
    try {
      await api.post('/auth/forgot-password', { email: user.email });
      setLink('sent');
    } catch (error) {
      setLink(errorMessage(t, error, 'common.actionFailed'));
    }
  };
  return (
    <>
      <PageHeader breadcrumb={[sectionCrumb(t, 'settings'), { label: t('sections.account') }]} title={t('sections.account')} />
      <div className="settings-stack">
        <Card as="section" aria-labelledby="account-who" className="account-card">
          <span className="shell-account__avatar account-card__avatar" aria-hidden="true">{initials(user.display_name || user.email)}</span>
          <div className="account-card__who">
            <h2 id="account-who" className="settings-card__title">{user.display_name}</h2>
            <p className="settings-muted">{user.email}</p>
            <p className="settings-muted">{t(isAdmin ? 'shell.roleAdmin' : 'shell.roleUser')}</p>
          </div>
        </Card>

        <Card as="section" aria-labelledby="account-language">
          <h2 id="account-language" className="settings-card__title">{t('language.label')}</h2>
          <p className="settings-muted">{t('sections.languageHelp')}</p>
          <div className="settings-row"><LanguageSwitcher className="lang-switcher--large" /></div>
        </Card>

        <Card as="section" aria-labelledby="account-password">
          <h2 id="account-password" className="settings-card__title">{t('sections.password')}</h2>
          <p className="settings-muted">{t('sections.passwordHelp', { email: user.email })}</p>
          <div className="settings-row">
            <Button variant="secondary" icon={MailIcon} onClick={sendLink} loading={link === 'sending'} disabled={link === 'sent'}>
              {t('sections.passwordLink')}
            </Button>
          </div>
          {link === 'sent' && <Alert tone="success">{t('sections.passwordLinkSent', { email: user.email })}</Alert>}
          {link && link !== 'sent' && link !== 'sending' && <Alert tone="danger">{link}</Alert>}
        </Card>

        <div className="settings-actions">
          <Button variant="secondary" icon={MailIcon} onClick={onInvite}>{t('nav.invite')}</Button>
          <Button variant="secondary" icon={LogoutIcon} onClick={onLogout}>{t('nav.logout')}</Button>
        </div>
      </div>
    </>
  );
}

export default AccountSettings;
